import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Ticket, 
  Search, 
  CheckCircle, 
  Clock, 
  Trophy,
  DollarSign,
  Filter,
  RefreshCw
} from 'lucide-react';

// Status colors and labels
const STATUS_CONFIG = {
  pending: { label: 'Pendiente', color: 'bg-amber-100 text-amber-800', icon: Clock },
  validated: { label: 'Validado', color: 'bg-blue-100 text-blue-800', icon: CheckCircle },
  rewarded: { label: 'Premiado', color: 'bg-purple-100 text-purple-800', icon: Trophy },
  paid: { label: 'Pagado', color: 'bg-green-100 text-green-800', icon: DollarSign }
};

export default function AdminTickets() {
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [updating, setUpdating] = useState(null);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const data = await api.admin.getAllTickets();
      setTickets(data.tickets || []);
      setFilteredTickets(data.tickets || []);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      toast.error('Error al cargar tickets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  // Filter tickets based on search and status
  useEffect(() => {
    let filtered = tickets;
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(t => 
        t.number?.toLowerCase().includes(term) ||
        t.reference?.toLowerCase().includes(term) ||
        t.user_name?.toLowerCase().includes(term) ||
        t.raffle_name?.toLowerCase().includes(term)
      );
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(t => t.status === statusFilter);
    }
    
    setFilteredTickets(filtered);
  }, [searchTerm, statusFilter, tickets]);

  const handleUpdateStatus = async (ticketId, newStatus) => {
    setUpdating(ticketId);
    try {
      await api.admin.updateTicketStatus(ticketId, newStatus);
      toast.success(`Ticket actualizado a: ${STATUS_CONFIG[newStatus].label}`);
      // Update local state
      setTickets(prev => prev.map(t => 
        t.id === ticketId ? { ...t, status: newStatus } : t
      ));
    } catch (error) {
      toast.error('Error al actualizar ticket');
    } finally {
      setUpdating(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('es-CO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Check if ticket is a triple (special prize)
  const isTripleTicket = (number) => {
    if (!number || number.length !== 3) return false;
    return number[0] === number[1] && number[1] === number[2];
  };

  if (loading) {
    return (
      <div className="page-container pt-6 space-y-4">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-12 w-full rounded-xl" />
        <Skeleton className="h-32 w-full rounded-2xl" />
        <Skeleton className="h-32 w-full rounded-2xl" />
      </div>
    );
  }

  return (
    <div className="page-container pt-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/admin">
            <Button variant="ghost" size="icon" className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <h1 className="heading-2">Gestión de Tickets</h1>
        </div>
        <Button variant="outline" size="icon" onClick={fetchTickets}>
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="bg-slate-50">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold">{tickets.length}</p>
            <p className="text-xs text-muted-foreground">Total</p>
          </CardContent>
        </Card>
        <Card className="bg-amber-50">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-amber-700">
              {tickets.filter(t => t.status === 'pending').length}
            </p>
            <p className="text-xs text-amber-600">Pendientes</p>
          </CardContent>
        </Card>
        <Card className="bg-purple-50">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-purple-700">
              {tickets.filter(t => t.status === 'rewarded').length}
            </p>
            <p className="text-xs text-purple-600">Premiados</p>
          </CardContent>
        </Card>
        <Card className="bg-green-50">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-green-700">
              {tickets.filter(t => t.status === 'paid').length}
            </p>
            <p className="text-xs text-green-600">Pagados</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            placeholder="Buscar por número, referencia, usuario..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-tickets"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-10 px-3 rounded-lg border border-slate-200 bg-white"
          data-testid="filter-status"
        >
          <option value="all">Todos los estados</option>
          <option value="pending">Pendientes</option>
          <option value="validated">Validados</option>
          <option value="rewarded">Premiados</option>
          <option value="paid">Pagados</option>
        </select>
      </div>

      {/* Triple Tickets Info */}
      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="p-4">
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-amber-600" />
            <span className="text-sm text-amber-800">
              Los tickets con números triples (000, 111, 222...) tienen un 
              <strong> premio especial de $10.000</strong>. Están marcados con un indicador especial.
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Tickets List */}
      <div className="space-y-3">
        {filteredTickets.length === 0 ? (
          <Card className="card-base">
            <CardContent className="p-8 text-center">
              <Ticket className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">No se encontraron tickets</p>
            </CardContent>
          </Card>
        ) : (
          filteredTickets.map((ticket) => {
            const isTriple = isTripleTicket(ticket.number);
            const statusConfig = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.pending;
            const StatusIcon = statusConfig.icon;
            
            return (
              <Card 
                key={ticket.id} 
                className={`card-base ${isTriple ? 'ring-2 ring-amber-400' : ''}`}
                data-testid={`ticket-${ticket.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    {/* Left side - Ticket info */}
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-3">
                        <span className="text-3xl font-mono font-bold text-primary">
                          {ticket.number}
                        </span>
                        {isTriple && (
                          <span className="px-2 py-1 bg-amber-200 text-amber-900 text-xs font-bold rounded-full animate-pulse">
                            🎉 TRIPLE
                          </span>
                        )}
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusConfig.color}`}>
                          <StatusIcon className="w-3 h-3 inline mr-1" />
                          {statusConfig.label}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                        <div>
                          <span className="text-muted-foreground">Sorteo:</span>{' '}
                          <span className="font-medium">{ticket.raffle_name || '-'}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Referencia:</span>{' '}
                          <span className="font-mono">{ticket.reference || '-'}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Usuario:</span>{' '}
                          <span className="font-medium">{ticket.user_name || '-'}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Método:</span>{' '}
                          <span className="capitalize">{ticket.payment_method || '-'}</span>
                        </div>
                        <div className="col-span-2">
                          <span className="text-muted-foreground">Fecha:</span>{' '}
                          <span>{formatDate(ticket.created_at)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Right side - Actions */}
                    <div className="flex flex-col gap-2">
                      {ticket.status !== 'validated' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleUpdateStatus(ticket.id, 'validated')}
                          disabled={updating === ticket.id}
                          className="text-blue-600 border-blue-200 hover:bg-blue-50"
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Validar
                        </Button>
                      )}
                      {ticket.status !== 'rewarded' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleUpdateStatus(ticket.id, 'rewarded')}
                          disabled={updating === ticket.id}
                          className="text-purple-600 border-purple-200 hover:bg-purple-50"
                        >
                          <Trophy className="w-4 h-4 mr-1" />
                          Premiado
                        </Button>
                      )}
                      {ticket.status !== 'paid' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleUpdateStatus(ticket.id, 'paid')}
                          disabled={updating === ticket.id}
                          className="text-green-600 border-green-200 hover:bg-green-50"
                        >
                          <DollarSign className="w-4 h-4 mr-1" />
                          Pagado
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Pagination info */}
      {filteredTickets.length > 0 && (
        <p className="text-center text-sm text-muted-foreground">
          Mostrando {filteredTickets.length} de {tickets.length} tickets
        </p>
      )}
    </div>
  );
}
